export type Preset = "today" | "yesterday" | "7d" | "30d" | "90d" | "180d" | "1y" | "all";

export type SelectedPeriod = {
  readonly since: string | null;
  readonly until: string | null;
  readonly preset: Preset | "custom";
};

export type PeriodValidation =
  | { readonly kind: "preset" }
  | { readonly kind: "exact"; readonly since: string; readonly until: string }
  | { readonly kind: "invalid"; readonly message: string };

const PRESETS: readonly Preset[] = ["today", "yesterday", "7d", "30d", "90d", "180d", "1y", "all"];

function isPreset(value: string | null): value is Preset {
  return value !== null && PRESETS.includes(value as Preset);
}

type UtcInstant = readonly [number, number, number, number, number, number, number];

const UTC_INSTANT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?(Z|\+00:00)$/;

function daysInMonth(year: number, month: number): number {
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function utcInstant(value: string): UtcInstant | null {
  const matched = UTC_INSTANT.exec(value);
  if (matched === null) return null;
  const [, yearText, monthText, dayText, hourText, minuteText, secondText = "0", fractionText = "0"] = matched;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const second = Number(secondText);
  const microsecond = Number(fractionText.padEnd(6, "0"));
  if (
    month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month) ||
    hour > 23 || minute > 59 || second > 59
  ) return null;
  return [year, month, day, hour, minute, second, microsecond];
}

export function isUtcInstant(value: string): boolean {
  return utcInstant(value) !== null;
}

function compare(left: UtcInstant, right: UtcInstant): number {
  const parts = [
    [left[0], right[0]], [left[1], right[1]], [left[2], right[2]], [left[3], right[3]],
    [left[4], right[4]], [left[5], right[5]], [left[6], right[6]],
  ] as const;
  for (const [leftPart, rightPart] of parts) {
    if (leftPart !== rightPart) return leftPart < rightPart ? -1 : 1;
  }
  return 0;
}

export function validatePeriod(period: SelectedPeriod): PeriodValidation {
  if (period.since === null && period.until === null) {
    return period.preset === "custom"
      ? { kind: "invalid", message: "Укажите обе границы периода в UTC." }
      : { kind: "preset" };
  }
  if (period.since === null || period.until === null) {
    return { kind: "invalid", message: "Укажите обе границы периода в UTC." };
  }
  const since = utcInstant(period.since);
  const until = utcInstant(period.until);
  if (since === null || until === null) {
    return { kind: "invalid", message: "Укажите корректные границы периода в UTC." };
  }
  if (compare(since, until) >= 0) {
    return { kind: "invalid", message: "Окончание периода должно быть позже начала." };
  }
  return { kind: "exact", since: period.since, until: period.until };
}

export function readPeriod(params: URLSearchParams): SelectedPeriod {
  const since = params.get("since");
  const until = params.get("until");
  const named = params.get("preset");

  if (since !== null || until !== null) {
    const period = { since, until, preset: isPreset(named) ? named : "custom" } as const;
    return validatePeriod(period).kind === "invalid" ? { ...period, preset: "custom" } : period;
  }
  return { since: null, until: null, preset: isPreset(named) ? named : "30d" };
}

export function writePeriod(period: SelectedPeriod): URLSearchParams {
  const params = new URLSearchParams({ preset: period.preset });
  if (period.since !== null) params.set("since", period.since);
  if (period.until !== null) params.set("until", period.until);
  return params;
}

/** What to call the selected span in a heading. One source, so a strip and the
 *  control above it can never name the same range differently. */
export function periodLabel(period: SelectedPeriod): string {
  const NAMES: Record<Preset, string> = {
    today: "сегодня",
    yesterday: "вчера",
    "7d": "7 дней",
    "30d": "30 дней",
    "90d": "90 дней",
    "180d": "6 месяцев",
    "1y": "1 год",
    all: "всё время",
  };
  return period.preset === "custom" ? "выбранный период" : NAMES[period.preset];
}
